import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import { TimeframeType } from 'webapp/enums/enums';
import { calculateAndFormatChange, calculateChange, getIndicatorClass } from 'webapp/utils/number';

import type { CompareDisplayData } from 'webapp/components/block/content/common/compares';
import MetricDetailsTopDatapointsComponent from 'webapp/components/metric-details/top-datapoints';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';
import MetricDetailsTopDatapointsForecastPreviousPeriodComponent from 'webapp/components/metric-details/top-datapoints/forecast-previous-period';

import type { Forecast } from 'webapp/models-custom';
import type DsDataPointTopLevel from 'webapp/models-custom/ds-data/point/top';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	forecast: Forecast;
	isLoading: boolean;
	isRefreshing: boolean;
}

export default class MetricDetailsTopDatapointsForecastDatapointsComponent extends Component<Args> {
	// ─── Forecast card ───────────────────────────────────────────────────

	get forecastLabel(): string {
		const forecast = this.args.forecast;
		if (!forecast) {
			return 'Forecast';
		}
		const timeframeStr = forecast.getTimeframeDisplayString();
		return `Forecast for ${timeframeStr}`;
	}

	get forecastValuePoint(): DChartPrimaryPoint {
		return new DChartPrimaryPoint({ valueStr: this.lastForecastPoint?.valueStr });
	}

	// ─── Actual vs Forecast card ─────────────────────────────────────────

	get lastForecastPoint(): DsDataPointTopLevel | undefined {
		const ds = this.args.forecast?.forecastWholeRangeDataSelect;
		return ds?.outputs?.[0]?.points?.find((p) => p.hasValue);
	}

	get lastCurrentPoint(): DsDataPointTopLevel | undefined {
		const ds = this.args.forecast?.currentValueDataSelect;
		return ds?.outputs?.[0]?.points?.find((p) => p.hasValue);
	}

	get isInFuture(): boolean {
		const forecastStartTimestamp = this.lastForecastPoint?.ts;
		return !!forecastStartTimestamp && Date.now() < forecastStartTimestamp;
	}

	get showActualVsForecast(): boolean {
		if (this.isInFuture) {
			return false;
		}
		return (
			!!this.lastCurrentPoint?.hasValue &&
			!!this.lastForecastPoint?.hasValue &&
			!!this.actualVsForecastChange
		);
	}

	get actualVsForecastLabel(): string {
		const timeframe = this.args.forecast?.timeframe;
		if (!timeframe) {
			return 'Month to date';
		}
		switch (timeframe.type) {
			case TimeframeType.Y:
				return 'Year to date';
			case TimeframeType.Q1:
			case TimeframeType.Q2:
			case TimeframeType.Q3:
			case TimeframeType.Q4:
				return 'Quarter to date';
			default:
				return 'Month to date';
		}
	}

	get actualVsForecastValuePoint(): DChartPrimaryPoint {
		return new DChartPrimaryPoint({ valueStr: this.lastCurrentPoint?.valueStr });
	}

	@cached
	get actualVsForecastChange() {
		const forecastPoint = this.lastForecastPoint;
		const currentPoint = this.lastCurrentPoint;

		if (!forecastPoint || !currentPoint?.hasValue) {
			return null;
		}

		const metric = this.args.forecast?.metricSetup?.metric;
		if (!metric) {
			return null;
		}

		const changeFormat = metric.defaults.changeFormat;
		const changeFunction = metric.defaults.changeFunction;

		// forecast is base, current is compare: if current > forecast, change is positive
		const change = calculateChange(forecastPoint.value, currentPoint.value, changeFunction);
		if (change === null) {
			return null;
		}

		const changeStr = calculateAndFormatChange(
			forecastPoint.value,
			currentPoint.value,
			changeFormat,
			changeFunction,
		);

		const indicatorClass = getIndicatorClass(change, !!metric.changeInvert);
		return { changeStr, indicatorClass };
	}

	get actualVsForecastComparePoints(): CompareDisplayData[] | undefined {
		const changeData = this.actualVsForecastChange;
		if (!changeData) {
			return undefined;
		}

		const forecastValueStr = this.lastForecastPoint?.valueStr;
		return [
			{
				changeStr: changeData.changeStr,
				indicatorClass: changeData.indicatorClass,
				compareText: forecastValueStr ? `vs forecast (${forecastValueStr})` : 'vs forecast',
			},
		];
	}

	<template>
		{{#if @isLoading}}
			<MetricDetailsTopDatapointsComponent>
				<MetricDetailsTopDatapointsDatapointCardComponent
					@label="Forecast"
					@icon="Forecasts"
					@valueClass="forecast"
					@size="primary"
					@isLoading={{true}}
				/>
				<MetricDetailsTopDatapointsForecastPreviousPeriodComponent
					@forecast={{@forecast}}
					@isLoading={{true}}
					@isRefreshing={{@isRefreshing}}
				/>
				{{#if this.showActualVsForecast}}
					<MetricDetailsTopDatapointsDatapointCardComponent
						@label={{this.actualVsForecastLabel}}
						@valueClass="accent"
						@isLoading={{true}}
					/>
				{{/if}}
			</MetricDetailsTopDatapointsComponent>
		{{else}}
			<div class={{if @isRefreshing "forecast-content-refreshing"}}>
				<MetricDetailsTopDatapointsComponent>
					<MetricDetailsTopDatapointsDatapointCardComponent
						@label={{this.forecastLabel}}
						@icon="Forecasts"
						@valueClass="forecast"
						@size="primary"
						@valuePoint={{this.forecastValuePoint}}
						@isLoading={{false}}
					/>
					<MetricDetailsTopDatapointsForecastPreviousPeriodComponent
						@forecast={{@forecast}}
						@isLoading={{false}}
						@isRefreshing={{@isRefreshing}}
					/>
					{{#if this.showActualVsForecast}}
						<MetricDetailsTopDatapointsDatapointCardComponent
							@label={{this.actualVsForecastLabel}}
							@valueClass="accent"
							@valuePoint={{this.actualVsForecastValuePoint}}
							@comparePoints={{this.actualVsForecastComparePoints}}
							@isLoading={{false}}
						/>
					{{/if}}
				</MetricDetailsTopDatapointsComponent>
			</div>
		{{/if}}
	</template>
}
